import { getTurnConfig } from '$lib/actions/auth.js';
import { registerCallReceiver, registerCallSync, sendCallSignal } from '$lib/websocket-client.js';

const MEDIA_DEFAULTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  },
  video: {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    frameRate: { ideal: 30, max: 30 },
    facingMode: 'user'
  }
};

const ROUTE_MONITOR_INTERVAL_MS = 5000;
const ROUTE_UPGRADE_BASE_MS = 60000;
const ROUTE_UPGRADE_MAX_MS = 5 * 60000;
const RECOVERY_INTERVAL_MS = 5000;
const TURN_REFRESH_MARGIN_MS = 5 * 60 * 1000;
const OFFER_RETRY_MS = 8000;

export function createWebRTCSession({
  conversationId,
  sessionId = null,
  callType = 'video',
  direction = 'outgoing',
  iceTransportPolicy = 'all',
  onLocalStream = () => { },
  onRemoteStream = () => { },
  onStatus = () => { },
  onRemoteScreenShare = () => { },
  onEnded = () => { },
  onError = () => { }
}) {
  let peer = null;
  let localStream = null;
  let remoteStream = new MediaStream();
  let cameraTrack = null;
  let screenTrack = null;
  let unsubscribe = null;
  let unsubscribeCallSync = null;
  let closed = false;
  let makingOffer = false;
  let pendingSignals = [];
  let pendingCandidates = [];
  let recoveryTimer = null;
  let networkRestartTimer = null;
  let reconnectRequestTimer = null;
  let routeMonitorTimer = null;
  let turnRefreshTimer = null;
  let turnExpiresAt = 0;
  let pendingResume = false;
  let offerSentAt = 0;
  let acceptedBeforeReady = false;
  let activeSessionId = sessionId;
  let activeIceTransportPolicy = normalizeIceTransportPolicy(iceTransportPolicy);
  let selectedRoute = 'unknown';
  let lastRouteProbeAt = 0;
  let routeProbeAttempts = 0;

  function isMediaConnected() {
    return peer?.connectionState === 'connected'
      && ['connected', 'completed'].includes(peer.iceConnectionState);
  }

  function matchesSession(event, { adopt = false } = {}) {
    if (!event?.sessionId) return true;
    if (!activeSessionId && adopt) activeSessionId = event.sessionId;
    return !activeSessionId || activeSessionId === event.sessionId;
  }

  async function start() {
    if (closed || peer) return;

    unsubscribe = registerCallReceiver({
      onCallAccepted: async (cid, event) => {
        if (cid !== conversationId || direction !== 'outgoing' || !matchesSession(event, { adopt: true })) return;
        if (!peer) {
          acceptedBeforeReady = true;
          return;
        }
        await createAndSendOffer();
      },
      onSignal: async (cid, signal, event) => {
        if (cid !== conversationId || !matchesSession(event, { adopt: true })) return;
        if (!peer) {
          pendingSignals.push(signal);
          return;
        }
        await handleSignal(signal);
      },
      onCallEnded: (cid, event) => {
        if (cid !== conversationId || !activeSessionId || !matchesSession(event)) return;
        close();
        onEnded(event);
      },
      onCallResumed: (cid, event) => {
        if (cid !== conversationId || !matchesSession(event)) return;
        if (!peer) {
          pendingResume = true;
          return;
        }
        // The previous socket may have lost the offer after it was sent.
        offerSentAt = 0;
        refreshTurnConfig().catch(() => { });
        scheduleIceRestart('socket-resumed');
      }
    });
    unsubscribeCallSync = registerCallSync(() => peer?.remoteDescription
      ? { conversationId, sessionId: activeSessionId }
      : null);

    try {
      onStatus('requesting-media');
      const turnConfig = await getTurnConfig({ iceTransportPolicy: activeIceTransportPolicy });
      activeIceTransportPolicy = normalizeIceTransportPolicy(turnConfig.iceTransportPolicy || activeIceTransportPolicy);
      if (activeIceTransportPolicy === 'relay' && !hasRelayIceServer(turnConfig.iceServers)) {
        const error = new Error('Không lấy được cấu hình TURN relay cho cuộc gọi.');
        error.name = 'TurnUnavailableError';
        throw error;
      }
      localStream = await navigator.mediaDevices.getUserMedia({
        audio: MEDIA_DEFAULTS.audio,
        video: callType === 'video' ? MEDIA_DEFAULTS.video : false
      });

      cameraTrack = localStream.getVideoTracks()[0] || null;
      onLocalStream(localStream);

      peer = new RTCPeerConnection({
        iceServers: turnConfig.iceServers,
        iceCandidatePoolSize: 4,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceTransportPolicy: activeIceTransportPolicy
      });
      scheduleTurnRefresh(turnConfig);

      localStream.getTracks().forEach((track) => peer.addTrack(track, localStream));

      peer.ontrack = ({ track, streams }) => {
        if (streams[0]) {
          remoteStream = streams[0];
        } else if (!remoteStream.getTrackById(track.id)) {
          remoteStream.addTrack(track);
        }
        onRemoteStream(remoteStream);
      };

      peer.onicecandidate = ({ candidate }) => {
        if (candidate) sendCallSignal(conversationId, { candidate });
      };

      peer.onconnectionstatechange = () => {
        const state = peer?.connectionState || 'closed';
        onStatus(state);

        if (state === 'connected') {
          stopRecovery();
          clearReconnectRequestTimer();
          if (!lastRouteProbeAt) lastRouteProbeAt = Date.now();
          startRouteMonitor();
        }
        if (state === 'failed' || state === 'disconnected') {
          startRecovery();
        }
        if (state === 'closed') onEnded({ reason: 'connection-closed' });
      };

      peer.oniceconnectionstatechange = () => {
        if (peer?.iceConnectionState === 'disconnected' || peer?.iceConnectionState === 'failed') {
          onStatus('reconnecting');
          startRecovery();
        } else if (isMediaConnected()) {
          stopRecovery();
          clearReconnectRequestTimer();
          onStatus('connected');
        }
      };

      attachNetworkListeners();

      for (const signal of pendingSignals.splice(0)) {
        await handleSignal(signal);
      }

      onStatus(direction === 'outgoing' ? 'ringing' : 'connecting');
      if (acceptedBeforeReady && direction === 'outgoing') {
        await createAndSendOffer();
      }
      if (pendingResume) {
        pendingResume = false;
        scheduleIceRestart('socket-resumed');
      }
    } catch (error) {
      onError(normalizeMediaError(error));
      close();
      throw error;
    }
  }

  async function createAndSendOffer({ iceRestart = false } = {}) {
    if (!peer || makingOffer || closed || peer.signalingState !== 'stable') return false;
    makingOffer = true;

    try {
      const offer = await peer.createOffer({ iceRestart });
      await peer.setLocalDescription(offer);
      if (!sendCallSignal(conversationId, { sdp: peer.localDescription })) {
        await peer.setLocalDescription({ type: 'rollback' });
        return false;
      }
      offerSentAt = Date.now();
      return true;
    } finally {
      makingOffer = false;
    }
  }

  async function handleSignal(signal = {}) {
    if (!peer || closed) return;

    if (signal.control?.type === 'screen-share') {
      onRemoteScreenShare(Boolean(signal.control.active));
      return;
    }

    if (signal.control?.type === 'restart-ice') {
      if (direction === 'outgoing') scheduleIceRestart('remote-request');
      return;
    }

    if (signal.sdp) {
      await peer.setRemoteDescription(signal.sdp);
      if (signal.sdp.type === 'answer') offerSentAt = 0;
      await flushCandidates();

      if (signal.sdp.type === 'offer') {
        const answer = await peer.createAnswer();
        await peer.setLocalDescription(answer);
        sendCallSignal(conversationId, { sdp: peer.localDescription });
      }
      return;
    }

    if (signal.candidate) {
      if (!peer.remoteDescription) {
        pendingCandidates.push(signal.candidate);
        return;
      }
      await addCandidate(signal.candidate);
    }
  }

  async function flushCandidates() {
    for (const candidate of pendingCandidates.splice(0)) {
      await addCandidate(candidate);
    }
  }

  async function addCandidate(candidate) {
    try {
      await peer?.addIceCandidate(candidate);
    } catch (error) {
      onError({ message: 'Không thể kết nối ICE candidate.', cause: error });
    }
  }

  async function restartIce() {
    if (!peer || closed || !peer.remoteDescription) return false;
    // An offer can be lost when the socket changes networks after send().
    if (peer.signalingState === 'have-local-offer' && Date.now() - offerSentAt >= OFFER_RETRY_MS) {
      await peer.setLocalDescription({ type: 'rollback' });
      offerSentAt = 0;
    }
    peer.restartIce?.();
    return createAndSendOffer({ iceRestart: true });
  }

  function startRouteMonitor() {
    if (routeMonitorTimer || closed) return;

    updateSelectedRoute().catch(() => { });
    routeMonitorTimer = setInterval(() => {
      updateSelectedRoute().catch(() => { });
    }, ROUTE_MONITOR_INTERVAL_MS);
  }

  function stopRouteMonitor() {
    clearInterval(routeMonitorTimer);
    routeMonitorTimer = null;
  }

  async function updateSelectedRoute() {
    if (!peer || closed || peer.connectionState !== 'connected') return;

    const route = await readSelectedRoute();
    if (route === 'unknown') return;

    if (route !== selectedRoute && route !== 'host') {
      lastRouteProbeAt = Date.now();
      routeProbeAttempts = 0;
    }
    selectedRoute = route;
    if (route === 'host') {
      routeProbeAttempts = 0;
      return;
    }

    // Some mobile browsers do not report a 5G-to-Wi-Fi change while relay
    // stays connected. Probe occasionally without interrupting the current path.
    if (direction !== 'outgoing' || activeIceTransportPolicy === 'relay' || document.hidden) return;
    const delay = Math.min(ROUTE_UPGRADE_BASE_MS * 2 ** routeProbeAttempts, ROUTE_UPGRADE_MAX_MS);
    if (Date.now() - lastRouteProbeAt < delay || peer.signalingState !== 'stable') return;
    lastRouteProbeAt = Date.now();
    routeProbeAttempts = Math.min(routeProbeAttempts + 1, 3);
    scheduleIceRestart('route-upgrade');
  }

  async function readSelectedRoute() {
    const report = await peer?.getStats();
    if (!report) return 'unknown';

    let selectedPair = null;
    let transport = null;
    const byId = new Map();

    report.forEach((stat) => {
      byId.set(stat.id, stat);
      if (stat.type === 'transport' && stat.selectedCandidatePairId) transport = stat;
      if (stat.type === 'candidate-pair' && stat.nominated && stat.state === 'succeeded') selectedPair = stat;
    });

    if (transport?.selectedCandidatePairId) {
      selectedPair = byId.get(transport.selectedCandidatePairId) || selectedPair;
    }

    const localCandidate = selectedPair ? byId.get(selectedPair.localCandidateId) : null;
    const remoteCandidate = selectedPair ? byId.get(selectedPair.remoteCandidateId) : null;
    const routes = [localCandidate, remoteCandidate].map((candidate) => normalizeRoute(candidate?.candidateType));
    if (routes.includes('relay')) return 'relay';
    if (routes.includes('srflx')) return 'srflx';
    return routes.includes('host') ? 'host' : 'unknown';
  }

  function scheduleIceRestart(source = 'network') {
    if (direction !== 'outgoing') {
      requestRemoteIceRestart(source);
      return;
    }
    if (closed || !peer || !peer.remoteDescription || networkRestartTimer) return;

    // The caller drives ICE restarts to avoid offer glare when both peers switch networks together.
    networkRestartTimer = setTimeout(() => {
      networkRestartTimer = null;
      if (closed || !peer || (isMediaConnected() && source === 'recovery')) return;
      if (!isMediaConnected()) onStatus('reconnecting');
      restartIce().then((sent) => {
        if (!sent && !isMediaConnected()) startRecovery();
      }).catch((error) => {
        console.warn('[call] ICE restart failed; will retry', error);
        startRecovery();
      });
    }, source === 'network' ? 350 : 250);
  }

  function requestRemoteIceRestart(source = 'network') {
    if (closed || reconnectRequestTimer) return;

    sendCallSignal(conversationId, { control: { type: 'restart-ice', source } });

    reconnectRequestTimer = setTimeout(() => {
      reconnectRequestTimer = null;
      if (closed || isMediaConnected()) return;
      requestRemoteIceRestart(source);
    }, 3000);
  }

  function clearReconnectRequestTimer() {
    clearTimeout(reconnectRequestTimer);
    reconnectRequestTimer = null;
  }

  function attachNetworkListeners() {
    window.addEventListener?.('online', handleNetworkAvailable);
    window.addEventListener?.('focus', handleVisibilityChange);
    window.addEventListener?.('pageshow', handleVisibilityChange);
    navigator.connection?.addEventListener?.('change', handleNetworkAvailable);
    document.addEventListener?.('visibilitychange', handleVisibilityChange);
  }

  function detachNetworkListeners() {
    window.removeEventListener?.('online', handleNetworkAvailable);
    window.removeEventListener?.('focus', handleVisibilityChange);
    window.removeEventListener?.('pageshow', handleVisibilityChange);
    navigator.connection?.removeEventListener?.('change', handleNetworkAvailable);
    document.removeEventListener?.('visibilitychange', handleVisibilityChange);
  }

  function handleNetworkAvailable() {
    lastRouteProbeAt = Date.now();
    routeProbeAttempts = 0;
    scheduleIceRestart('network');
    if (!isMediaConnected()) startRecovery();
  }

  function handleVisibilityChange() {
    if (!document.hidden && (!isMediaConnected() || selectedRoute !== 'host')) {
      lastRouteProbeAt = Date.now();
      routeProbeAttempts = 0;
      scheduleIceRestart('network');
    }
  }

  function startRecovery() {
    if (recoveryTimer || closed) return;
    onStatus('reconnecting');
    scheduleIceRestart('recovery');
    recoveryTimer = setInterval(() => {
      if (isMediaConnected()) return stopRecovery();
      scheduleIceRestart('recovery');
    }, RECOVERY_INTERVAL_MS);
  }

  function stopRecovery() {
    clearInterval(recoveryTimer);
    recoveryTimer = null;
  }

  function scheduleTurnRefresh(config) {
    clearTimeout(turnRefreshTimer);
    turnExpiresAt = config?.relayAvailable && Number(config.ttl) > 0
      ? Date.now() + Number(config.ttl) * 1000
      : 0;
    const delay = turnExpiresAt ? Math.max(30000, turnExpiresAt - Date.now() - TURN_REFRESH_MARGIN_MS) : 30000;
    turnRefreshTimer = setTimeout(() => refreshTurnConfig().catch(() => { }), delay);
  }

  async function refreshTurnConfig() {
    if (!peer || closed) return;
    try {
      const config = await getTurnConfig({ iceTransportPolicy: activeIceTransportPolicy });
      if (closed || !peer) return;
      if (!config.relayAvailable || !hasRelayIceServer(config.iceServers)) throw new Error('TURN unavailable');
      peer.setConfiguration({ ...peer.getConfiguration(), iceServers: config.iceServers });
      scheduleTurnRefresh(config);
      if (selectedRoute === 'relay') scheduleIceRestart('turn-refresh');
    } catch (error) {
      if (closed) return;
      console.warn('[call] TURN refresh failed; will retry', error);
      clearTimeout(turnRefreshTimer);
      turnRefreshTimer = setTimeout(() => refreshTurnConfig().catch(() => { }), 30000);
    }
  }

  function setAudioEnabled(enabled) {
    localStream?.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  function setVideoEnabled(enabled) {
    cameraTrack && (cameraTrack.enabled = enabled);
  }

  async function replaceInput(kind, deviceId) {
    if (!deviceId || !peer || closed) return;

    const constraints = kind === 'audio'
      ? { audio: { ...MEDIA_DEFAULTS.audio, deviceId: { exact: deviceId } }, video: false }
      : { audio: false, video: { ...MEDIA_DEFAULTS.video, deviceId: { exact: deviceId } } };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    const nextTrack = kind === 'audio' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
    const sender = peer.getSenders().find(({ track }) => track?.kind === kind);
    const oldTrack = kind === 'audio' ? localStream.getAudioTracks()[0] : cameraTrack;

    if (kind !== 'video' || !screenTrack) {
      await sender?.replaceTrack(nextTrack);
    }
    if (oldTrack) {
      localStream.removeTrack(oldTrack);
      oldTrack.stop();
    }
    localStream.addTrack(nextTrack);

    if (kind === 'video') cameraTrack = nextTrack;
    onLocalStream(localStream);
  }

  async function startScreenShare() {
    if (callType !== 'video' || screenTrack || !peer) return false;

    const display = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: 24, max: 30 } },
      audio: false
    });
    screenTrack = display.getVideoTracks()[0];

    const sender = peer.getSenders().find(({ track }) => track?.kind === 'video');
    await sender?.replaceTrack(screenTrack);
    screenTrack.onended = () => stopScreenShare();
    sendCallSignal(conversationId, { control: { type: 'screen-share', active: true } });
    return true;
  }

  async function stopScreenShare() {
    if (!screenTrack || !peer) return;

    const sender = peer.getSenders().find(({ track }) => track?.kind === 'video');
    await sender?.replaceTrack(cameraTrack);
    screenTrack.onended = null;
    screenTrack.stop();
    screenTrack = null;
    sendCallSignal(conversationId, { control: { type: 'screen-share', active: false } });
  }

  async function getStats() {
    if (!peer || closed) return null;
    return peer.getStats();
  }

  function close() {
    if (closed) return;
    closed = true;

    stopRecovery();
    clearTimeout(networkRestartTimer);
    clearTimeout(turnRefreshTimer);
    networkRestartTimer = null;
    turnRefreshTimer = null;
    clearReconnectRequestTimer();
    stopRouteMonitor();
    detachNetworkListeners();
    unsubscribe?.();
    unsubscribe = null;
    unsubscribeCallSync?.();
    unsubscribeCallSync = null;
    if (screenTrack) {
      screenTrack.onended = null;
      screenTrack.stop();
    }
    localStream?.getTracks().forEach((track) => track.stop());
    remoteStream?.getTracks().forEach((track) => track.stop());

    if (peer) {
      peer.ontrack = null;
      peer.onicecandidate = null;
      peer.onconnectionstatechange = null;
      peer.oniceconnectionstatechange = null;
      peer.close();
    }

    peer = null;
    localStream = null;
    remoteStream = null;
  }

  return {
    start,
    close,
    restartIce,
    setAudioEnabled,
    setVideoEnabled,
    replaceInput,
    startScreenShare,
    stopScreenShare,
    getStats
  };
}

function normalizeMediaError(error) {
  const messages = {
    NotAllowedError: 'Bạn chưa cấp quyền camera hoặc microphone.',
    NotFoundError: 'Không tìm thấy camera hoặc microphone phù hợp.',
    NotReadableError: 'Camera hoặc microphone đang được ứng dụng khác sử dụng.',
    OverconstrainedError: 'Thiết bị không hỗ trợ cấu hình được yêu cầu.',
    TurnUnavailableError: 'TURN relay chưa sẵn sàng, không thể bắt đầu cuộc gọi ở chế độ relay-only.'
  };

  return {
    message: messages[error?.name] || 'Không thể khởi tạo cuộc gọi.',
    cause: error
  };
}

function normalizeIceTransportPolicy(value) {
  const policy = String(value || 'all').trim().toLowerCase();
  return policy === 'relay' || policy === 'relay-only' ? 'relay' : 'all';
}

function normalizeRoute(candidateType) {
  if (candidateType === 'prflx') return 'srflx';
  return ['host', 'srflx', 'relay'].includes(candidateType) ? candidateType : 'unknown';
}

function hasRelayIceServer(iceServers = []) {
  return iceServers.some((server) => {
    const urls = Array.isArray(server?.urls) ? server.urls : [server?.urls];
    return urls.some((url) => /^turns?:/i.test(String(url || '')));
  });
}
